import { useEffect, useState } from "react";
import { Link } from "wouter";
import { AlertTriangle, Info, Loader2, Radio, RefreshCw, X } from "lucide-react";
import { formatTimeAgo, type AiEngine } from "@/lib/ai-engine";
import { AgentFace } from "@/components/agent-face";
import type { WorkerStatusView } from "@/hooks/use-worker-status";
import { isActiveJob, type PendingChatJob } from "@/hooks/use-chat-jobs";

/**
 * UI for the claude_worker engine: status chip, "no worker" banner and the
 * queued / failed / timed-out Draper bubbles. Theme-aware (uses foreground/chat tokens)
 * so it works in every Draper chat surface.
 */

function useNow(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

// ── Worker status chip ───────────────────────────────────────────────────────

type Tone = "green" | "amber" | "grey";

const TONE_CLASSES: Record<Tone, { chip: string; dot: string }> = {
  green: { chip: "bg-emerald-500/10 border-emerald-500/25 text-emerald-400", dot: "bg-emerald-400" },
  amber: { chip: "bg-amber-500/10 border-amber-500/25 text-amber-400", dot: "bg-amber-400" },
  grey: { chip: "bg-foreground/5 border-foreground/10 text-muted-foreground", dot: "bg-foreground/30" },
};

/** "Claude worker · last seen 1m ago · 2 queued" (hybrid: "Claude team · ..."). Renders nothing for the Gemini engine. */
export function WorkerStatusChip({ status, className = "" }: { status: WorkerStatusView; className?: string }) {
  if (!status.enabled) return null;

  const tone: Tone = status.health === "fresh" ? "green" : status.health === "stale" ? "amber" : "grey";

  let label = status.engine === "hybrid" ? "Claude team" : "Claude worker";
  if (status.isError) label += " · status unavailable";
  else if (status.health === "unknown") label += " · checking…";
  else if (status.health === "never") label += " · never seen";
  else label += ` · last seen ${formatTimeAgo(status.lastSeenAt)}`;
  if (status.queued > 0) label += ` · ${status.queued} queued`;
  if (status.claimed > 0) label += ` · ${status.claimed} working`;

  const workers = status.data?.workers ?? [];
  const title = workers.length
    ? workers
        .map((w) => `${w.workerId}${w.note ? ` — ${w.note}` : ""}${typeof w.runs === "number" ? ` (${w.runs} runs)` : ""}, last seen ${formatTimeAgo(w.lastSeenAt)}`)
        .join("\n")
    : "No Claude worker has checked in yet.";

  const t = TONE_CLASSES[tone];
  return (
    <div
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold border max-w-full ${t.chip} ${className}`}
      title={title}
      data-testid="chip-worker-status"
      data-health={status.health}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${t.dot}`} />
      <span className="truncate">{label}</span>
    </div>
  );
}

// ── "No worker" banner ───────────────────────────────────────────────────────

/**
 * Subtle, non-blocking notice shown when no worker has checked in within 10 minutes.
 * In hybrid chat is instant, so the notice only matters while the Claude team has work waiting.
 */
export function WorkerDelayBanner({ status, className = "px-5" }: { status: WorkerStatusView; className?: string }) {
  if (!status.shouldWarn) return null;
  const hybrid = status.engine === "hybrid";
  if (hybrid && status.active === 0) return null;
  return (
    <div
      className={`shrink-0 flex items-center gap-2 py-1.5 text-[11px] text-amber-400/90 bg-amber-500/5 border-b border-amber-500/10 ${className}`}
      role="status"
      data-testid="banner-worker-delay"
    >
      <Info size={11} className="shrink-0" />
      <span>
        {hybrid
          ? "The Claude team hasn't checked in recently — chat is instant, but the work you handed off may be delayed"
          : "No worker has checked in recently — replies may be delayed"}
      </span>
    </div>
  );
}

// ── Hybrid: "Work in progress" strip ─────────────────────────────────────────

/**
 * Shown above the chat input in hybrid while the Claude team has work queued or in progress.
 * Counts come from GET /api/workers/status (queued + claimed work jobs). Renders nothing otherwise.
 */
export function WorkInProgressStrip({ status, className = "" }: { status: WorkerStatusView; className?: string }) {
  if (status.engine !== "hybrid" || !status.enabled || status.active === 0) return null;
  const n = status.active;
  const waiting = status.claimed === 0;
  return (
    <div
      className={`flex items-start gap-2 rounded-xl border border-cyan-500/20 bg-cyan-500/[0.06] px-3 py-2 text-[12px] leading-snug text-foreground/80 ${className}`}
      role="status"
      aria-live="polite"
      data-testid="strip-work-in-progress"
      data-active={n}
    >
      <Loader2 size={13} className="mt-0.5 shrink-0 animate-spin text-cyan-400" />
      <p className="min-w-0">
        <span className="font-semibold text-foreground">
          Claude team is working on {n} {n === 1 ? "item" : "items"}
        </span>
        {" "}— results post here and under{" "}
        <Link href="/content?tab=review" className="whitespace-nowrap text-cyan-300 underline underline-offset-2" data-testid="link-strip-needs-review">
          Needs your review
        </Link>
        .
        {waiting && status.queued > 0 && (
          <span className="ml-1 text-foreground/50">Waiting for a worker to pick {n === 1 ? "it" : "them"} up.</span>
        )}
      </p>
    </div>
  );
}

// ── Queued / failed / timed-out bubbles ──────────────────────────────────────

function DraperAvatar({ tone }: { tone: "blue" | "amber" | "red" }) {
  if (tone === "blue") return <AgentFace agent="draper" mood="thinking" size={30} />;
  const cls = tone === "red" ? "bg-red-500/10 border-red-500/25" : "bg-amber-500/10 border-amber-500/25";
  return (
    <div className={`w-7 h-7 rounded-xl border flex items-center justify-center shrink-0 ${cls}`}>
      <AlertTriangle size={13} className={tone === "red" ? "text-red-400" : "text-amber-400"} />
    </div>
  );
}

function QueuedJobBubble({ jobs, latestActivity }: { jobs: PendingChatJob[]; latestActivity?: string }) {
  const now = useNow(15_000);
  const oldest = jobs[0];
  const pickedUp = jobs.some((j) => j.status === "claimed");
  const extra = jobs.length - 1;

  return (
    <div className="flex gap-2.5 animate-in fade-in duration-300" data-testid="bubble-queued-job" data-job-status={pickedUp ? "claimed" : "queued"}>
      <DraperAvatar tone="blue" />
      <div className="max-w-[82%] flex-1">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400">Draper</span>
          <span
            className="flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] font-bold"
            style={{ background: "rgba(34,211,238,0.06)", borderColor: "rgba(34,211,238,0.2)", color: "#22d3ee" }}
          >
            <Radio size={7} className="animate-pulse" /> {pickedUp ? "Working" : "Queued"}
          </span>
        </div>
        <div
          className="rounded-2xl rounded-tl-sm px-4 py-3 flex items-start gap-2.5"
          style={{ background: "var(--chat-thinking-bg)", border: "1px solid var(--chat-thinking-border)" }}
        >
          <Loader2 size={12} className="animate-spin text-blue-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-sm text-foreground/70 leading-snug">
              Draper is working on this — replies usually land within a couple of minutes.
            </p>
            <p className="text-[11px] text-foreground/40 mt-1 leading-snug">
              {pickedUp ? "A worker picked this up" : "Waiting for a worker to pick this up"} · sent {formatTimeAgo(oldest.startedAt, now)}
              {extra > 0 ? ` · +${extra} more queued, answered in order` : ""}
            </p>
            {latestActivity && (
              <p className="text-[11px] text-foreground/40 mt-0.5 leading-snug truncate">{latestActivity}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StoppedJobBubble({ job, onRetry, onDismiss }: {
  job: PendingChatJob;
  onRetry: (job: PendingChatJob) => void;
  onDismiss: (jobId: string) => void;
}) {
  const timedOut = job.status === "timeout";
  const accent = timedOut
    ? { box: "bg-amber-500/5 border-amber-500/20", btn: "bg-amber-500/10 border-amber-500/25 text-amber-400 hover:bg-amber-500/20", label: "text-amber-400" }
    : { box: "bg-red-500/5 border-red-500/20", btn: "bg-red-500/10 border-red-500/20 text-red-400 hover:bg-red-500/20", label: "text-red-400" };

  return (
    <div className="flex gap-2.5 animate-in fade-in duration-300" data-testid={timedOut ? "bubble-timeout-job" : "bubble-failed-job"}>
      <DraperAvatar tone={timedOut ? "amber" : "red"} />
      <div className="max-w-[82%] flex-1">
        <div className="flex items-center gap-2 mb-1.5">
          <span className="text-[10px] font-bold uppercase tracking-wider text-blue-400">Draper</span>
          <span className={`text-[9px] font-bold uppercase tracking-wider ${accent.label}`}>{timedOut ? "Taking longer than expected" : "Couldn't finish"}</span>
        </div>
        <div className={`rounded-2xl rounded-tl-sm px-4 py-3 border ${accent.box}`}>
          <p className="text-sm text-foreground/70 leading-snug break-words">
            {timedOut
              ? "This is taking longer than usual — the worker may be offline. You can keep waiting (a late reply will still appear here) or send it again."
              : `Draper couldn't finish this one: ${job.error || "something went wrong on the worker."}`}
          </p>
          {job.messageText && (
            <p className="text-[11px] text-foreground/40 mt-1 truncate" data-testid="text-job-excerpt">
              Re: “{job.messageText.replace(/\s+/g, " ").slice(0, 90)}”
            </p>
          )}
          <div className="flex items-center gap-2 mt-2.5">
            <button
              onClick={() => onRetry(job)}
              className={`flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-bold border transition-all ${accent.btn}`}
              data-testid="button-retry-job"
            >
              <RefreshCw size={9} /> Retry
            </button>
            <button
              onClick={() => onDismiss(job.jobId)}
              className="flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium text-foreground/40 hover:text-foreground/70 transition-colors"
              data-testid="button-dismiss-job"
            >
              <X size={9} /> Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Renders all pending-job UI for one thread: a single "Draper is working on this" bubble for the
 * queued/claimed messages (with a "+N more" note when several are waiting), plus one bubble per
 * failed or timed-out job with Retry / Dismiss.
 */
export function ChatJobBubbles({ jobs, onRetry, onDismiss, latestActivity, engine }: {
  jobs: PendingChatJob[];
  onRetry: (job: PendingChatJob) => void;
  onDismiss: (jobId: string) => void;
  latestActivity?: string;
  /** In hybrid, chat is instant, so the "Draper is working on this" bubble never shows (only failed/timed-out ones do). */
  engine?: AiEngine;
}) {
  const active = engine === "hybrid" ? [] : jobs.filter(isActiveJob);
  const stopped = jobs.filter((j) => !isActiveJob(j));
  if (active.length === 0 && stopped.length === 0) return null;
  return (
    <>
      {active.length > 0 && <QueuedJobBubble jobs={active} latestActivity={latestActivity} />}
      {stopped.map((job) => (
        <StoppedJobBubble key={job.jobId} job={job} onRetry={onRetry} onDismiss={onDismiss} />
      ))}
    </>
  );
}
